/**
 * Contact mutation hooks using generic factory
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { contactApi } from '@/lib/api/contacts.api';
import { queryKeys } from '../query-keys';
import { createResourceMutations } from './createResourceMutations';
import { contactModuleCountsKey } from '../useContactModuleCounts';
import { toast } from 'sonner';
import type { Contact, CreateContact, UpdateContact } from '@scrambl/shared';

/**
 * Generate all contact mutation hooks using the factory
 */
const mutations = createResourceMutations<Contact, CreateContact, UpdateContact>({
  resourceName: 'Contact',
  resourceNameSingular: 'contact',
  queryKeyFactory: queryKeys.contacts,
  api: {
    create: (data: CreateContact) => contactApi.createContact(data),
    update: (id: string, data: UpdateContact) => contactApi.updateContact(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = { [field]: value } as UpdateContact;
      return contactApi.updateContact(id, updateData);
    },
    delete: (id: string) => contactApi.deleteContact(id),
  },
  enableRealtime: false,
  deferredEntityType: 'contact',
  invalidation: {
    onCreate: { invalidateRelated: [[...contactModuleCountsKey]] },
    onDelete: { invalidateRelated: [[...contactModuleCountsKey]] },
  },
});

/**
 * Export individual hooks with original names for backward compatibility
 */
export const useCreateContactMutation = mutations.useCreateMutation;
export const useUpdateContactMutation = mutations.useUpdateMutation;
export const useUpdateContactFieldMutation = mutations.useUpdateFieldMutation!;
export const useDeleteContactMutation = mutations.useDeleteMutation;

/**
 * Bulk delete contacts mutation hook
 */
export function useConfirmProvisionalContactsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => contactApi.confirmProvisionalContacts(ids),
    onSuccess: (result) => {
      const { confirmedContacts, confirmedEntities } = result;
      toast.success(
        `${confirmedContacts} contact${confirmedContacts === 1 ? '' : 's'} confirmed` +
          (confirmedEntities > 0
            ? ` and ${confirmedEntities} compan${confirmedEntities === 1 ? 'y' : 'ies'}`
            : ''),
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.lists() });
      queryClient.invalidateQueries({ queryKey: contactModuleCountsKey });
    },
    onError: (error) => {
      toast.error('Failed to confirm contacts');
      console.error('Confirm provisional contacts error:', error);
    },
  });
}

export function useBulkDeleteContactsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => contactApi.bulkDeleteContacts(ids),
    onSuccess: (result) => {
      toast.success(`${result.deletedCount} contact${result.deletedCount === 1 ? '' : 's'} deleted`);
      // Invalidate contacts list and module counts
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.lists() });
      queryClient.invalidateQueries({ queryKey: contactModuleCountsKey });
    },
    onError: (error) => {
      toast.error('Failed to delete contacts');
      console.error('Bulk delete contacts error:', error);
    },
  });
}
